import React, { useState, useEffect, useRef } from 'react';
import { Target, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier1Store from '../../../stores/useAtelier1Store';
import useAtelier2Store from '../../../stores/useAtelier2Store';
import useAtelier3Store from '../../../stores/useAtelier3Store';
import StrategicScenariosTable from './StrategicScenariosTable';
import { showLoadingToast, dismissToast, showErrorToast } from '../../../utils/toastUtils';
import { useSilentSaveListener } from '../../../hooks/useSilentSaveListener';
import GuideModal from './GuideModal';
import securityControlsService from '../../../services/securityControlsService';

const Activite2 = (props) => {
  const { t } = useTranslation();
  // ... (states)
  const [mappings, setMappings] = useState([]);
  const [sourcesRisque, setSourcesRisque] = useState([]);
  const [dreadedEvents, setDreadedEvents] = useState([]);
  const [controlsData, setControlsData] = useState(null);
  const [stakeholders, setStakeholders] = useState([]);
  const [selectedStakeholders, setSelectedStakeholders] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const tableRef = useRef(null);

  useSilentSaveListener('atelier3-activite2', () => { if (tableRef.current) return tableRef.current.silentSave(); return 'skipped'; });

  // Guide state
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // Get analysis context
  const analysisStore = useAnalysisStore();
  const atelier1Store = useAtelier1Store();
  const atelier2Store = useAtelier2Store();
  const atelier3Store = useAtelier3Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const getSourcesDeRisque = atelier2Store.getSourcesDeRisque;
  const getSelectedCouplesSROV = atelier2Store.getSelectedCouplesSROV;
  const currentDreadedEvents = atelier1Store.dreadedEvents;
  const getStakeholders = atelier3Store.loadStakeholders;
  const currentBusinessValues = atelier1Store.businessValues;

  // Load data on component mount
  useEffect(() => {
    console.log('🚀 ACTIVITE2 useEffect triggered with analysis:', currentAnalysis?.id);
    const fetchData = async () => {
      if (!currentAnalysis?.id) {
        console.log('❌ No analysis ID found, skipping data load');
        return;
      }

      setIsLoading(true);
      setError(null);
      const toastId = showLoadingToast(t('workshop3.activity2.loading'));

      try {
        // Load sources de risque
        const sourcesData = await getSourcesDeRisque(currentAnalysis.id);
        if (sourcesData) {
          setSourcesRisque(sourcesData);
        }

        // Load dreaded events
        if (currentDreadedEvents && currentDreadedEvents.length > 0) {
          setDreadedEvents(currentDreadedEvents);
        }

        // Load existing mappings
        const mappingsData = await getSelectedCouplesSROV(currentAnalysis.id);
        if (mappingsData) {
          setMappings(mappingsData);
        }

        // Load stakeholders
        try {
          const stakeholdersResponse = await getStakeholders(currentAnalysis.id);
          if (stakeholdersResponse && stakeholdersResponse.success && Array.isArray(stakeholdersResponse.data)) {
            // Filter to only include retained stakeholders
            setStakeholders(stakeholdersResponse.data.filter(s => s.retained === true));
          } else {
            console.log('Stakeholders data is not valid:', stakeholdersResponse);
            setStakeholders([]);
          }
        } catch (stakeholderError) {
          console.error('Error loading stakeholders:', stakeholderError);
          setStakeholders([]);
        }


        // Load security controls data
        try {
          const controlsResponse = await securityControlsService.getSecurityControls(currentAnalysis.id);
          if (controlsResponse) {
            setControlsData(controlsResponse);
          }
        } catch (controlsError) {
          console.error('Error loading security controls:', controlsError);
          // Don't fail the whole load if controls can't be loaded
          setControlsData({ planData: {} });
        }

        // Dismiss the loading toast without showing a success toast
        dismissToast(toastId);
      } catch (error) {
        console.error('Error loading data:', error);
        setError(t('workshop3.activity2.errors.loadingError'));
        // Dismiss the loading toast and show error toast
        dismissToast(toastId);
        showErrorToast(t('workshop3.activity2.errors.loadingDataError'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentAnalysis?.id]); // Only depend on the analysis ID

  // Separate effect to update dreaded events when they change in context
  useEffect(() => {
    if (currentDreadedEvents && currentDreadedEvents.length > 0) {
      setDreadedEvents(currentDreadedEvents);
    }
  }, [currentDreadedEvents]);

  return (
    <div className="space-y-8 p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      {/* Loading state */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-slate-50 rounded-xl shadow-sm">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">{t('workshop3.activity2.loadingMessage')}</p>
          </div>
        </div>
      ) : error ? (
        <div className="p-6 bg-slate-50 rounded-lg">
          <div className="bg-red-100 border border-red-300 text-red-800 p-4 rounded-lg mb-4 shadow-sm">
            <h3 className="font-bold mb-2 text-lg flex items-center">
              <Target size={20} className="mr-2" />
              {t('workshop3.activity2.errors.loadingErrorTitle')}
            </h3>
            <p className="mb-3">{error}</p>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 text-sm"
              onClick={() => window.location.reload()}
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      ) : (
        <StrategicScenariosTable
          ref={tableRef}
          mappings={mappings}
          sourcesRisque={sourcesRisque}
          dreadedEvents={dreadedEvents}
          controlsData={controlsData}
          stakeholders={stakeholders}
          businessValues={currentBusinessValues}
          initialSelectedStakeholders={selectedStakeholders}
          onSelectedStakeholdersChange={setSelectedStakeholders}
          onHelpClick={() => setIsGuideOpen(true)}
        />
      )}

      {/* Guide Modal - positioned under the header */}
      <GuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
    </div>
  );
};

export default Activite2;
